import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { errorToast } from "@/v2-ui/toast"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import Table, {
  createColumns,
  summaryFraction,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import CountryFlag from "@/v2-backoffice-client/country/CountryFlag"
import TranslationCountriesDetail from "@/v2-backoffice-client/translation/countries/TranslationCountriesDetail"
import TranslationCountriesSupportToggle from "@/v2-backoffice-client/translation/countries/TranslationCountriesSupportToggle"
import { useTranslationCountriesQuery } from "@/v2-backoffice-client/translation/countries/translation.countries.query"
import {
  countryReadiness,
  namedLanguageCount,
  type TranslationCountryRow,
} from "@/v2-backoffice-client/translation/countries/translation.countries.utils"

const COUNTRY_COLUMNS = createColumns<TranslationCountryRow>({
  enableSorting: true,
  columns: {
    supported: {
      id: "supported",
      label: "Supported",
      description:
        "Whether the country can be picked for a project or a user context, and offered in a feature's country availability list.",
      defaultSize: 120,
      getValue: (row) => (row.isSupported ? "Supported" : "Off"),
      renderCell({ row }) {
        return (
          <span className="flex items-center gap-2">
            <Dot
              variant={row.original.isSupported ? "success-1" : "muted"}
              className="shrink-0"
            />
            {row.original.isSupported ? "Supported" : "Off"}
          </span>
        )
      },
    },
    country: {
      id: "country",
      label: "Country",
      defaultSize: 260,
      getValue: (row) => `${row.name} ${row.alpha2} ${row.alpha3} ${row.code}`,
      renderCell({ row }) {
        return (
          <span className="flex min-w-0 items-center gap-2">
            <CountryFlag
              alpha2={row.original.alpha2}
              name={row.original.name}
            />
            <span className="flex min-w-0 flex-col">
              <span className="truncate">{row.original.name}</span>
              <span className="truncate text-xs text-muted-foreground">
                {row.original.alpha2} · {row.original.alpha3} ·{" "}
                {row.original.code}
              </span>
            </span>
          </span>
        )
      },
    },
    readiness: {
      id: "readiness",
      label: "Readiness",
      description:
        "Whether the country has the map geometry a market needs. Nothing falls back for a missing polygon, so the map would have no extent to open the country at. Names are tracked separately — the console falls back to English for those.",
      defaultSize: 170,
      // Unready first when ascending, so the gaps group together.
      getValue: (row) => (countryReadiness(row).isReady ? 1 : 0),
      renderCell({ row }) {
        const readiness = countryReadiness(row.original)
        return (
          <span
            className={cn(
              "flex items-center gap-2",
              readiness.isReady && "text-muted-foreground",
            )}
          >
            <Dot
              variant={readiness.isReady ? "success-1" : "warning"}
              className="shrink-0"
            />
            {readiness.label}
          </span>
        )
      },
      summaryValue({ row }) {
        return countryReadiness(row.original).label
      },
    },
    names: {
      id: "names",
      label: "Names",
      description:
        "Supported languages the country has a name in. Country names live on the country row, not in the translation catalog, so the translation queues never surface these gaps — and the console falls back to English, so a gap degrades the label rather than breaking it.",
      defaultSize: 110,
      getValue: (row) => namedLanguageCount(row),
      renderCell({ row }) {
        const named = namedLanguageCount(row.original)
        const isComplete = named === row.original.supportedLanguageCount
        return (
          <span
            className={cn(!isComplete && "text-muted-foreground")}
            title={
              isComplete
                ? undefined
                : `No name in ${row.original.missingLanguages.join(", ")}`
            }
          >
            {named} / {row.original.supportedLanguageCount}
          </span>
        )
      },
      summaryValue({ row }) {
        const named = namedLanguageCount(row.original)
        return summaryFraction(named, row.original.supportedLanguageCount)
      },
    },
    content: {
      id: "content",
      label: "Targeted at",
      description:
        "Features, basemaps and nav layers aimed specifically AT this country (excluded ones in brackets). A feature with no country rows at all is offered everywhere, so a new market inherits the global catalog without appearing here.",
      defaultSize: 130,
      getValue: (row) => row.includedFeatureCount,
      renderCell({ row }) {
        return (
          <span
            className={cn(
              row.original.includedFeatureCount === 0 &&
                "text-muted-foreground",
            )}
          >
            {row.original.includedFeatureCount.toLocaleString("en-GB")}
            {row.original.excludedFeatureCount > 0
              ? ` (${row.original.excludedFeatureCount} excluded)`
              : ""}
          </span>
        )
      },
    },
    active: {
      id: "active",
      label: "Active",
      description:
        "Retired countries leave every list the apps read, not just the markets — pickers, availability lists and country validation. Lookups by a known code still resolve, so existing records keep their country name.",
      defaultSize: 90,
      getValue: (row) => (row.isActive ? "Active" : "Retired"),
      renderCell({ row }) {
        if (row.original.isActive) {
          return <span className="text-muted-foreground">Active</span>
        }
        return <span className="text-destructive">Retired</span>
      },
    },
    actions: {
      id: "actions",
      label: "",
      defaultSize: 120,
      getValue: () => "",
      renderCell({ row }) {
        return <TranslationCountriesSupportToggle row={row.original} />
      },
    },
  },
})

export type TranslationCountriesViewProps = Omit<
  TableProps<TranslationCountryRow>,
  "data" | "columns"
>

/**
 * "Countries" — the country registry and the market switch behind it.
 *
 * Reads and writes the AUTHORING catalog, unlike every picker in the
 * backoffice, which reads this environment's runtime. location.Countries is
 * MERGEd from countries.json on every deployment (unmatched rows are
 * deleted), so a runtime write would be reverted; authoring writes survive
 * because the publish rebuilds that blob from them. Same shape as the
 * Languages registry next to it: a small controlled vocabulary whose flag
 * turns on capability platform-wide.
 */
function TranslationCountriesView(props: TranslationCountriesViewProps) {
  const { className, ...tableProps } = props

  const [expandedCode, setExpandedCode] = useState<string | null>(null)

  const { data, loading, error } = useTranslationCountriesQuery()
  errorToast(error, { title: "Failed to load countries" })

  const rows: TranslationCountryRow[] = (data?.getCountryCoverage ?? []).map(
    (country) => ({
      code: country.Code,
      alpha2: country.Alpha2,
      alpha3: country.Alpha3,
      name: country.Name ?? country.Alpha2,
      isSupported: country.IsSupported,
      isActive: country.IsActive,
      hasPolygon: country.HasPolygon,
      names: country.Names.map((name) => ({
        languageCode: name.LanguageCode,
        name: name.Name,
      })),
      missingLanguages: country.MissingLanguages,
      supportedLanguageCount: country.SupportedLanguageCount,
      includedFeatureCount: country.IncludedFeatureCount,
      excludedFeatureCount: country.ExcludedFeatureCount,
    }),
  )

  return (
    <Table
      className={cn("translation-countries-table", className)}
      tableId="translation-countries-table"
      data={rows}
      columns={COUNTRY_COLUMNS}
      // Supported first (the column sorts on its own label, so "Supported"
      // beats "Off" only descending), then the countries closest to being
      // ready, then the ones with the most content already aimed at them.
      initialSorting={[
        { id: "supported", desc: true },
        { id: "readiness", desc: false },
        { id: "content", desc: true },
      ]}
      getRowId={(row) => String(row.code)}
      enableRowSelection={false}
      onRowClick={(row) =>
        setExpandedCode((previous) =>
          previous === String(row.original.code)
            ? null
            : String(row.original.code),
        )
      }
      expandedRowId={expandedCode}
      renderRowDetail={(row) => (
        <TranslationCountriesDetail row={row.original} />
      )}
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.Search placeholder="Search country..." />
        <div className="flex-1" />
        <Table.ToolbarMenu exportFileName="countries" />
      </Table.Toolbar>

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default TranslationCountriesView
